import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import useAuth from '@/hooks/useAuth';
import { Role } from '@/routes/PrivateRoute';
import ROUTES from '@/routes/routes';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import { DEBOUNCE_TIME } from '@/constants/general';

import {
  BillingAccountV2,
  DirectoryCustomer,
  OrderTemplateListItem,
  PaymentMethodsResponse,
} from '../api/types';

type EntryScreenProps = {
  billingAccounts: BillingAccountV2[];
  directoryCustomers: DirectoryCustomer[];
  onRefreshDirectory?: () => void;
  templates: OrderTemplateListItem[];
  selectedBillingAccountId: number | null;
  onSelectBillingAccount: (account: BillingAccountV2 | null) => void;
  onStartOrder: () => void;
  onPickTemplate: (templateId: number) => void;
  orderType: 'GIG' | 'LTA' | null;
  paymentMethods: PaymentMethodsResponse | null;
  paymentMethodsLoading: boolean;
  paymentMethodsError: string | null;
};

const PROTO_TEXT_SECONDARY = '#5B5B5B';
const PROTO_CARD_BG = '#FEFEFE';

const PROTO_GREEN = '#44A735';
const PROTO_GREEN_DARK = '#2E8722';
const PROTO_TEXT = '#1C1C1C';
const PROTO_TEXT_MUTED = '#979797';
const PROTO_BORDER = '#DADADA';
const PROTO_BORDER_LIGHT = '#EAEAEA';
const PROTO_HOVER_BG = '#F5F5F5';
const PROTO_PAGE_BG = '#FCFCFC';
const FONT = "'Manrope', sans-serif";

const EntryScreen = ({
  billingAccounts,
  directoryCustomers,
  onRefreshDirectory,
  templates,
  selectedBillingAccountId,
  onSelectBillingAccount,
  onStartOrder,
  onPickTemplate,
  orderType,
  paymentMethods,
  paymentMethodsLoading,
  paymentMethodsError,
}: EntryScreenProps) => {
  const { currentAdmin, currentAdminIsCustomerAdmin } = useAuth();
  const isTenantAdmin = currentAdmin?.role === Role.TENANT_ADMIN;

  const [billingOpen, setBillingOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [clientQuery, setClientQuery] = useState('');
  const [selectedClient, setSelectedClient] =
    useState<DirectoryCustomer | null>(null);
  const [error, setError] = useState<string | null>(null);

  const billingWrapRef = useRef<HTMLDivElement>(null);
  const clientWrapRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebouncedValue(clientQuery, DEBOUNCE_TIME);

  // Tenant admin: search clients locally against the directory payload.
  // The directory returns the full actor-scoped customer tree in one
  // call, so search/autocomplete runs entirely in memory.
  // For LTA orders, hide clients whose `lta_allowed` is not true — matches
  // v1's CustomerAutocompleteField filter, since backend rejects these
  // anyway with "LTA not allowed for this customer".
  const isLtaOrder = orderType === 'LTA';
  const eligibleCustomers = useMemo<DirectoryCustomer[]>(() => {
    if (!isLtaOrder) return directoryCustomers;
    return directoryCustomers.filter(
      (customer) => customer.lta_allowed === true,
    );
  }, [isLtaOrder, directoryCustomers]);

  const clientItems = useMemo<DirectoryCustomer[]>(() => {
    if (!isTenantAdmin) return [];
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return eligibleCustomers;
    return eligibleCustomers.filter((customer) =>
      (customer.name ?? '').toLowerCase().includes(q),
    );
  }, [isTenantAdmin, eligibleCustomers, debouncedQuery]);

  // Restore the visible Client selection from the retained billing
  // account. Exiting the wizard mid-order keeps `state.billingAccount`
  // in the parent (so Repeat orders can still render), but EntryScreen
  // remounts with `selectedClient` reset to null — leaving a tenant
  // admin with blank Client/Billing fields while repeat-order templates
  // still show. Re-derive the client from the selected account's
  // customer_id so the selection displays consistently again.
  useEffect(() => {
    if (!isTenantAdmin || selectedClient || selectedBillingAccountId == null) {
      return;
    }
    const account = billingAccounts.find(
      (a) => a.id === selectedBillingAccountId,
    );
    if (account?.customer_id == null) return;
    const customer = directoryCustomers.find(
      (c) => c.id === account.customer_id,
    );
    if (customer) setSelectedClient(customer);
  }, [
    isTenantAdmin,
    selectedClient,
    selectedBillingAccountId,
    billingAccounts,
    directoryCustomers,
  ]);

  // If the order type flips to LTA after a non-eligible client was selected
  // (e.g., the user navigates here via ?type=LTA after picking a client),
  // clear the selection so the wizard can't proceed with an invalid customer.
  useEffect(() => {
    if (!isTenantAdmin || !selectedClient) return;
    if (isLtaOrder && selectedClient.lta_allowed !== true) {
      setSelectedClient(null);
      onSelectBillingAccount(null);
    }
  }, [isTenantAdmin, isLtaOrder, selectedClient, onSelectBillingAccount]);

  // Close popovers on outside click
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (
        billingWrapRef.current &&
        !billingWrapRef.current.contains(e.target as Node)
      ) {
        setBillingOpen(false);
      }
      if (
        clientWrapRef.current &&
        !clientWrapRef.current.contains(e.target as Node)
      ) {
        setClientOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Tenant admins drill down: pick a client first, then see only that
  // client's accounts. Customer admins see the flat accounts list directly
  // (their directory scope is already restricted server-side).
  const accountsForCurrentScope: BillingAccountV2[] = useMemo(() => {
    if (!isTenantAdmin) return billingAccounts;
    if (!selectedClient) return [];
    return billingAccounts.filter(
      (account) => account.customer_id === selectedClient.id,
    );
  }, [isTenantAdmin, billingAccounts, selectedClient]);

  const selectedBillingAccount =
    accountsForCurrentScope.find((a) => a.id === selectedBillingAccountId) ||
    null;

  const handlePickClient = (customer: DirectoryCustomer) => {
    setSelectedClient(customer);
    setClientOpen(false);
    setClientQuery('');
    onSelectBillingAccount(null);
  };

  const handleStart = () => {
    if (!selectedBillingAccountId) {
      setError('Please select a billing account');
      return;
    }
    setError(null);
    onStartOrder();
  };

  // V1 parity: legacy Billing form disabled "Start order" until the selected
  // account had a `currentPaymentMethod`. We only treat the account as
  // missing once the fetch has resolved without an error — loading/error
  // states fall through so an in-flight request never hard-blocks the user.
  const paymentMethodMissing =
    !!selectedBillingAccountId &&
    !!paymentMethods &&
    !paymentMethodsLoading &&
    !paymentMethodsError &&
    !paymentMethods.current_payment_method;

  // Deep-link target for the "Click here" CTA, mirroring ReviewStep:
  // client admins manage their own company billing accounts under
  // /my-company, tenant admins under /clients/:customerId. Falls back to
  // null when we lack enough context to build the link.
  const accountSettingsPath = (() => {
    if (!selectedBillingAccount?.id) return null;
    if (currentAdminIsCustomerAdmin) {
      return `/${ROUTES.myCompany}/accounts/${selectedBillingAccount.id}`;
    }
    if (selectedBillingAccount.customer_id == null) return null;
    return `/${ROUTES.clients}/${selectedBillingAccount.customer_id}/accounts/${selectedBillingAccount.id}`;
  })();

  const canStart = !!selectedBillingAccountId && !paymentMethodMissing;
  const showRepeatOrders = !!selectedBillingAccountId && templates.length > 0;

  return (
    <div
      style={{
        background: PROTO_PAGE_BG,
        minHeight: '100%',
        width: '100%',
        padding: '32px 24px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            maxWidth: 708,
            margin: '0 0 24px 0',
          }}
        >
          <p
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: PROTO_TEXT,
              fontFamily: FONT,
              margin: 0,
            }}
          >
            New Order
          </p>
          {onRefreshDirectory ? (
            <button
              type="button"
              onClick={onRefreshDirectory}
              style={{
                background: 'transparent',
                border: 'none',
                color: PROTO_TEXT_SECONDARY,
                fontFamily: FONT,
                fontSize: 12,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Refresh customer list
            </button>
          ) : null}
        </div>

        <div
          style={{
            background: '#fff',
            border: `1px solid ${PROTO_BORDER}`,
            borderRadius: 8,
            padding: '24px 32px',
            width: '100%',
            maxWidth: 708,
            boxSizing: 'border-box',
          }}
        >
          {isTenantAdmin && (
            <div
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'flex-end',
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <p
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: PROTO_TEXT,
                    fontFamily: FONT,
                    margin: 0,
                  }}
                >
                  Client
                </p>
                <div ref={clientWrapRef} style={{ position: 'relative' }}>
                  <div
                    onClick={() => setClientOpen((v) => !v)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      height: 34,
                      border: `1px solid ${PROTO_BORDER}`,
                      borderRadius: 8,
                      padding: '0 8px',
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="text"
                      value={
                        clientOpen ? clientQuery : selectedClient?.name || ''
                      }
                      placeholder="Search for a client"
                      onChange={(e) => setClientQuery(e.target.value)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setClientOpen(true);
                      }}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: 14,
                        fontFamily: FONT,
                        color:
                          selectedClient && !clientOpen
                            ? PROTO_TEXT
                            : PROTO_TEXT_MUTED,
                        background: 'transparent',
                        cursor: 'text',
                      }}
                    />
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={PROTO_TEXT}
                      strokeWidth={2}
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                  {clientOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: `1px solid ${PROTO_BORDER_LIGHT}`,
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 100,
                        overflow: 'hidden',
                        maxHeight: 280,
                        overflowY: 'auto',
                      }}
                    >
                      {clientItems.length === 0 ? (
                        <div
                          style={{
                            padding: '10px 16px',
                            fontSize: 14,
                            color: PROTO_TEXT_MUTED,
                            fontFamily: FONT,
                          }}
                        >
                          No clients found
                        </div>
                      ) : (
                        clientItems.map((c, idx) => (
                          <div
                            key={c.id}
                            onClick={() => handlePickClient(c)}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.background =
                                PROTO_HOVER_BG)
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.background = 'transparent')
                            }
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              fontSize: 14,
                              color: PROTO_TEXT,
                              fontFamily: FONT,
                              borderBottom:
                                idx < clientItems.length - 1
                                  ? '1px solid #F5F5F5'
                                  : 'none',
                            }}
                          >
                            {c.name}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <p
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: PROTO_TEXT,
                  fontFamily: FONT,
                  margin: 0,
                }}
              >
                Billing account
              </p>
              <div ref={billingWrapRef} style={{ position: 'relative' }}>
                <div
                  onClick={() => {
                    if (isTenantAdmin && !selectedClient) return;
                    setBillingOpen((v) => !v);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 34,
                    border: `1px solid ${PROTO_BORDER}`,
                    borderRadius: 8,
                    padding: '0 8px',
                    background: '#fff',
                    cursor:
                      isTenantAdmin && !selectedClient
                        ? 'not-allowed'
                        : 'pointer',
                    opacity: isTenantAdmin && !selectedClient ? 0.6 : 1,
                  }}
                >
                  <input
                    type="text"
                    value={selectedBillingAccount?.name || ''}
                    placeholder={
                      isTenantAdmin && !selectedClient
                        ? 'Select a client first'
                        : 'Select your billing account'
                    }
                    readOnly
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: 14,
                      fontFamily: FONT,
                      color: selectedBillingAccount
                        ? PROTO_TEXT
                        : PROTO_TEXT_MUTED,
                      background: 'transparent',
                      cursor: 'inherit',
                    }}
                  />
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={PROTO_TEXT}
                    strokeWidth={2}
                    style={{ width: 20, height: 20, flexShrink: 0 }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {billingOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      border: `1px solid ${PROTO_BORDER_LIGHT}`,
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 100,
                      overflow: 'hidden',
                      maxHeight: 280,
                      overflowY: 'auto',
                    }}
                  >
                    {accountsForCurrentScope.length === 0 ? (
                      <div
                        style={{
                          padding: '10px 16px',
                          fontSize: 14,
                          color: PROTO_TEXT_MUTED,
                          fontFamily: FONT,
                        }}
                      >
                        No billing accounts available
                      </div>
                    ) : (
                      accountsForCurrentScope.map((account, idx) => (
                        <div
                          key={account.id}
                          onClick={() => {
                            onSelectBillingAccount(account);
                            setBillingOpen(false);
                            setError(null);
                          }}
                          onMouseOver={(e) =>
                            (e.currentTarget.style.background = PROTO_HOVER_BG)
                          }
                          onMouseOut={(e) =>
                            (e.currentTarget.style.background = 'transparent')
                          }
                          style={{
                            padding: '10px 16px',
                            cursor: 'pointer',
                            fontSize: 14,
                            color: PROTO_TEXT,
                            fontFamily: FONT,
                            borderBottom:
                              idx < accountsForCurrentScope.length - 1
                                ? '1px solid #F5F5F5'
                                : 'none',
                          }}
                        >
                          {account.name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleStart}
              disabled={!canStart}
              onMouseOver={(e) => {
                if (canStart)
                  e.currentTarget.style.background = PROTO_GREEN_DARK;
              }}
              onMouseOut={(e) => {
                if (canStart) e.currentTarget.style.background = PROTO_GREEN;
              }}
              style={{
                height: 32,
                padding: '0 16px',
                background: canStart ? PROTO_GREEN : '#A8D5A1',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                fontFamily: FONT,
                cursor: canStart ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
                flexShrink: 0,
                transition: 'background 0.15s',
              }}
            >
              Start order
            </button>
          </div>

          {showRepeatOrders && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                marginTop: 32,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span
                  style={{
                    fontSize: 14,
                    color: PROTO_TEXT_SECONDARY,
                    fontFamily: FONT,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Repeat orders
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: PROTO_BORDER,
                  }}
                />
              </div>
              {templates.map((template) => {
                const title =
                  template.primary_skill || `${template.order_type} order`;
                const address = template.location_label || '';
                const orderedLabel = `Ordered ${template.used_count}x`;
                return (
                  <div
                    key={template.id}
                    onClick={() => onPickTemplate(template.id)}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.borderColor = PROTO_GREEN)
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.borderColor = PROTO_BORDER)
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: 16,
                      border: `1px solid ${PROTO_BORDER}`,
                      borderRadius: 8,
                      background: PROTO_CARD_BG,
                      cursor: 'pointer',
                      transition: 'border-color 0.15s',
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={PROTO_GREEN}
                      strokeWidth={1.5}
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    >
                      <path
                        d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 3v5h5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: PROTO_TEXT,
                          margin: 0,
                          fontFamily: FONT,
                        }}
                      >
                        {title}
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginTop: 2,
                        }}
                      >
                        {address ? (
                          <span
                            style={{
                              fontSize: 14,
                              color: PROTO_TEXT_SECONDARY,
                              fontFamily: FONT,
                            }}
                          >
                            {address}
                          </span>
                        ) : null}
                        {address ? (
                          <span
                            style={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              background: PROTO_TEXT_SECONDARY,
                              flexShrink: 0,
                              display: 'inline-block',
                            }}
                          />
                        ) : null}
                        <span
                          style={{
                            fontSize: 14,
                            color: PROTO_TEXT_SECONDARY,
                            fontFamily: FONT,
                          }}
                        >
                          {orderedLabel}
                        </span>
                      </div>
                    </div>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={PROTO_TEXT}
                      strokeWidth={2}
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </div>
                );
              })}
            </div>
          )}

          {paymentMethodMissing && (
            <div
              role="alert"
              style={{
                marginTop: 16,
                padding: '10px 12px',
                borderRadius: 6,
                background: '#FDECEC',
                border: '1px solid #F5B7B7',
                color: '#A41B1B',
                fontSize: 13,
                lineHeight: 1.4,
                fontFamily: FONT,
              }}
            >
              <div>
                <strong>
                  There is no payment method associated with this billing
                  account.
                </strong>
              </div>
              <div style={{ marginTop: 4 }}>
                {accountSettingsPath ? (
                  <Link
                    to={accountSettingsPath}
                    style={{
                      color: '#A41B1B',
                      fontWeight: 700,
                      textDecoration: 'underline',
                    }}
                  >
                    Click here
                  </Link>
                ) : (
                  <strong>Click here</strong>
                )}{' '}
                to add payment method.
              </div>
            </div>
          )}

          {error && (
            <p
              style={{
                fontSize: 12,
                color: '#D32F2F',
                fontFamily: FONT,
                margin: '8px 0 0 0',
              }}
            >
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntryScreen;
